import type { RunStats } from './records';

/** A boss fight reference: which boss, at which level. */
export interface SavedFight {
  id: string;
  level: number;
}

/**
 * Run save — snapshotted at every shop break and at the death checkpoint
 * (docs/01). Loading resumes where it was taken (`resumeAt`). Stats/weapon
 * tier are NOT stored: they are re-derived by re-applying the owned card
 * collection to a fresh ship.
 *
 * Migration: every field added after v1 is OPTIONAL and the loader fills a
 * default — old saves without a boss bag start with their unseen bosses
 * (bosses/rotation.ts restore), without `resumeAt` they reopen the shop,
 * without `mode` they continue as an ENDLESS run (pre-sector saves).
 *
 * 4 independent save slots (docs/12 C3): each slot is its own "game".
 * The active slot is picked on the title screen; all save calls route to it.
 */
export interface SaveData {
  /** Run kind (absent in pre-sector saves = an endless run) */
  mode?: 'sector' | 'endless';
  /** Sector index of a sector run (0-based) */
  sector?: number;
  seed: number;
  roomNumber: number;
  arenaIndex: number;
  /** Recent boss history — no repeats across a bag refill (absent in old saves) */
  recentBossIds?: string[];
  /** Encounters per boss id this run (old saves: the per-boss ladder level) */
  bossNextLevel: Record<string, number>;
  /** Shuffle-bag: regular bosses still unseen this cycle (absent in old saves) */
  bossBag?: string[];
  /** Where to resume: the shop break or the failed room (absent = shop) */
  resumeAt?: 'shop' | 'fight';
  /** Fight in progress at a death checkpoint / the one just cleared */
  currentFight?: SavedFight;
  /** Pre-drawn next fight shown in the shop preview */
  nextPick?: SavedFight;
  gems: number;
  red: number;
  ownedCardIds: string[];
  inventory: string[];
  /** Crate waiting for swap-or-scrap (full bag, docs/09 wave 4) */
  pendingWeapon?: string;
  /** Active weapons in socket order (new format) */
  equippedIds?: string[];
  /** Legacy single/dual-weapon fields (old saves) */
  primaryId?: string;
  secondaryId?: string | null;
  hp: number;
  /** Armor units (absent in old saves) */
  armor?: number;
  /** Stored hangar armor plates (docs/12 H3) */
  storedArmor?: number;
  /** Attempt counter — starts at 1, +1 on every death (docs/12 C3) */
  totalRuns?: number;
  /** Wave checkpoint inside the current arena (docs/12 C8) */
  waveIndex?: number;
  /** Achievements earned so far in this run (victory screen list) */
  earned?: string[];
  stats: RunStats;
}

export const SAVE_SLOTS = 4;
const LEGACY_KEY = 'bosshot-save-v1';

let activeSlot = 0;

function slotKey(slot: number): string {
  return `${LEGACY_KEY}:s${slot}`;
}

/** Pre-slot saves become slot 1 the first time anything touches storage. */
function migrateLegacy(): void {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw !== null) {
      if (localStorage.getItem(slotKey(0)) === null) {
        localStorage.setItem(slotKey(0), raw);
      }
      localStorage.removeItem(LEGACY_KEY);
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

export function setActiveSlot(slot: number): void {
  activeSlot = Math.max(0, Math.min(SAVE_SLOTS - 1, slot));
}

export function getActiveSlot(): number {
  return activeSlot;
}

/** Peek any slot without changing the active one (title screen summaries). */
export function loadSlot(slot: number): SaveData | null {
  migrateLegacy();
  try {
    const raw = localStorage.getItem(slotKey(slot));
    return raw ? (JSON.parse(raw) as SaveData) : null;
  } catch {
    return null;
  }
}

export function clearSlot(slot: number): void {
  try {
    localStorage.removeItem(slotKey(slot));
  } catch {
    /* ignore */
  }
}

export function saveRun(data: SaveData): void {
  try {
    localStorage.setItem(slotKey(activeSlot), JSON.stringify(data));
  } catch {
    /* storage unavailable — ignore */
  }
}

export function loadRun(): SaveData | null {
  return loadSlot(activeSlot);
}

export function clearSave(): void {
  clearSlot(activeSlot);
}
